import { autorun, makeAutoObservable, runInAction } from "mobx";

import { AddressDTO } from "../../../Common/DTOs/Address/AddressDTO";
import { countryController } from "../../../Common/Controllers/CountryController";
import { alertController } from "../../../Common/Controllers/AlertController";
import { utils } from "../../../Common/Controllers/Utils";

export class AddressDialogController {
  formData: AddressDTO = {
    id: 0,
    firstName: "",
    surname: "",
    companyName: "",
    address1: "",
    address2: "",
    address3: "",
    address4: "",
    postCode: "",
    country: "",
    phoneDay: "",
    description: "Delivery Address",
  };

  loading = false;
  submitting = false;
  selectedCountryName = "";

  constructor() {
    makeAutoObservable(this);
  }

  initialize(initialData?: AddressDTO) {
    this.loading = true;
    this.submitting = false;

    const newFormData = initialData
      ? JSON.parse(JSON.stringify(initialData))
      : {
        id: 0,
        firstName: "",
        surname: "",
        companyName: "",
        address1: "",
        address2: "",
        address3: "",
        address4: "",
        postCode: "",
        country: "",
        phoneDay: "",
        description: "Delivery Address",
      };

    runInAction(() => {
      this.formData = newFormData;
    });

    this.resolveCountry(initialData?.country);

    runInAction(() => {
      this.loading = false;
    });
  }

  resolveCountry(incoming?: string) {
    const { countries } = countryController;

    if (countries.length === 0) {
      // Note: countries may load later - handle asynchronously
      const disposer = autorun(() => {
        if (countryController.countries.length > 0) {
          this.resolveCountry(incoming);
          disposer();
        }
      });
      return;
    }

    const match = countryController.findByIsoOrName(incoming);
    const resolved = match ? match.name : countries[0].name;

    runInAction(() => {
      this.selectedCountryName = resolved;
      this.formData.country = resolved;
    });
  }

  get descriptionError() {
    return this.formData.description !== null && this.formData.description !== undefined && (this.formData.description.length <= 0 || this.formData.description.length > 40);
  }

  get firstNameError() {
    return !!this.formData.firstName && (this.formData.firstName.length < 2 || this.formData.firstName.length > 20);
  }

  get surnameError() {
    return !!this.formData.surname && (this.formData.surname.length < 2 || this.formData.surname.length > 20);
  }

  get companyNameError() {
    return !!this.formData.companyName && (this.formData.companyName.length < 2 || this.formData.companyName.length > 50);
  }

  get address1Error() {
    return !!this.formData.address1 && (this.formData.address1.length < 2 || this.formData.address1.length > 30);
  }

  get address2Error() {
    return !!this.formData.address2 && (this.formData.address2.length > 30);
  }

  get address3Error() {
    return !!this.formData.address3 && (this.formData.address3.length < 2 || this.formData.address3.length > 30);
  }

  get address4Error() {
    return !!this.formData.address4 && (this.formData.address4.length < 2 || this.formData.address4.length > 30);
  }

  get postCodeError() {
    return !!this.formData.postCode && (this.formData.postCode.length < 2 || this.formData.postCode.length > 30);
  }

  get phoneError() {
    return !!this.formData.phoneDay && (this.formData.phoneDay.length > 0 && !utils.phoneNumberCorrect(this.formData.phoneDay))
  }

  get countryError() {
    return !this.formData.country;
  }

  get isSaveDisabled() {
    return (
      this.descriptionError || (this.formData.isBillingAddress && !this.formData.description) ||
      this.firstNameError || !this.formData.firstName ||
      this.surnameError || !this.formData.surname ||
      this.companyNameError || !this.formData.companyName ||
      this.address1Error || !this.formData.address1 ||
      this.address2Error ||
      this.address3Error || !this.formData.address3 ||
      (this.formData.country === "United States" ? !this.formData.address4 : this.address4Error) ||
      this.postCodeError || !this.formData.postCode ||
      this.phoneError || !this.formData.phoneDay ||
      this.countryError || !this.formData.country
    );
  }

  setValue<K extends keyof AddressDTO>(key: K, value: AddressDTO[K]) {
    runInAction(() => {
      this.formData[key] = value;
    });
  }

  handleCountryChange(name: string) {
    runInAction(() => {
      this.selectedCountryName = name;
      this.formData.country = name;
    });
  }

  async submit(onSubmit: (data: AddressDTO) => Promise<void>, onClose: () => void) {
    if (this.isSaveDisabled) {
      alertController.displayAlert("Invalid input", "Please correct highlighted fields.", "error");
      return;
    }

    this.submitting = true;
    try {
      await onSubmit(this.formData);
      onClose();
    } catch (err) {
      alertController.displayAlert("Error saving address", (err as Error).message, "error");
    } finally {
      runInAction(() => {
        this.submitting = false;
      });
    }
  }
}

export const addressDialogController = new AddressDialogController();
